import { AxiosResponse, ResponseType } from 'axios'
import { ChargingSessionExportRequestDto, ChargingSessionExportFormatDto } from './dto/charging-session-export-request.dto'
import { ChargingSessionExportParams } from '../models/charging-sessions-export.model'
import { AuthFetchWithResult } from '@e3dc-react/shell/Types/api'
import { TemplatesDto } from './dto/pdf-template.dto'
import { TemplatesModel } from '../models/pdf-template.model'
import { MIDMeterFilterValue } from '../types'
import { NOT_AUTHENTICATED_FLAG } from '../models/not-authenticated-flag'

const formatTypeMapper: Record<ChargingSessionExportFormatDto, ResponseType> = {
  pdf: 'blob',
  'pdf-energy': 'blob',
  xlsx: 'blob',
  csv: 'json',
  'pdf-reimbursement': 'blob',
}

/**
 * Reports about the completed charging sessions(Customer data reports or CDRs) are generated on BE side and fetched
 * over a single parametrized endpoint.
 *
 * We can choose:
 *
 * format: PDF, PDF no energy, CSV or Excel(xlsx)
 * date range
 * RFIDs of charge cards used to start the session
 * wallbox IDs of wallboxes which were used to charge the vehicle
 * customer info with which we populate the report
 * MID meter which can be with or without meter values
 * authentication info if sessions are created without authentication
 *
 * This endpoint only returns the data(text or binary) representing the file. Converting this data to
 * an actual file and starting the download is handled in a specialized hook "useExportChargingSessions".
 */
export const fetchChargingSessionReportFile = ({
  from,
  to,
  installationId,
  format,
  rfidFilter,
  wallboxIdFilter,
  midMeter,
  language,
  reimbursementPrice,
  authFetch,
}: ChargingSessionExportParams): Promise<AxiosResponse<string>> => {
  const tokenIds = rfidFilter.filter(filter => filter !== NOT_AUTHENTICATED_FLAG)
  /** per agreement with backend, distinguish "no rfid options selected" from "no-auth option selected + no rfids selected" by sending "null" and "[]", respectively as the tokenIds field*/
  const tokenIdPayload = rfidFilter.length === 0 ? null : tokenIds
  const unauthenticatedEnabled = rfidFilter.includes(NOT_AUTHENTICATED_FLAG) || rfidFilter.length === 0

  let midMeterPayloadValue: boolean | null
  const isWithMidMeterSelected = midMeter.some(meter => meter === MIDMeterFilterValue.withMIDMeter)
  const isWithoutMidMeterSelected = midMeter.some(meter => meter === MIDMeterFilterValue.withoutMIDMeter)

  if (midMeter.length === 0) {
    midMeterPayloadValue = null
  } else if (isWithMidMeterSelected && isWithoutMidMeterSelected) {
    midMeterPayloadValue = null
  } else if (isWithMidMeterSelected) {
    midMeterPayloadValue = true
  } else {
    midMeterPayloadValue = false
  }

  const payload: ChargingSessionExportRequestDto = {
    templateLocale: language,
    sessionFilterRequiredDatesRequest: {
      ocpiTokenIds: tokenIdPayload,
      wallboxIds: wallboxIdFilter,
      midEnabled: midMeterPayloadValue,
      unauthenticatedEnabled,
      startDateTimeFrom: from,
      startDateTimeTo: to,
    },
    reimbursementPrice,
  }

  return authFetch<string>({
    endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
    path: `cdrs/installation/${installationId}/${format}`,
    method: 'POST',
    payload,
    type: (() => formatTypeMapper[format])(),
  })
}

export interface FetchTemplatesParams {
  installationId: number
  authFetch: AuthFetchWithResult
}

export const fetchTemplates = ({ installationId, authFetch }: FetchTemplatesParams): Promise<TemplatesModel[]> => {
  return authFetch<TemplatesDto[]>({
    endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
    path: `templates/${installationId}/categories/languages`,
    method: 'GET',
  })
}
